import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, API_BASE } from "../lib/api";
import { useAuth } from "../lib/auth";
import PrivacyBadge from "../components/PrivacyBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "../components/ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
    DialogFooter,
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Upload, Download, Shield } from "lucide-react";

const KB_CATEGORY_LABEL = {
    governing: "Governing rule",
    precedent_successful: "Successful precedent",
    precedent_failed: "Failed precedent",
};

const KB_CATEGORY_STYLE = {
    governing: { bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE" },
    precedent_successful: { bg: "#ECFDF5", text: "#047857", border: "#A7F3D0" },
    precedent_failed: { bg: "#FEF2F2", text: "#B91C1C", border: "#FECACA" },
};

function CategoryPill({ value }) {
    if (!value) return null;
    const s = KB_CATEGORY_STYLE[value];
    if (!s) return null;
    return (
        <span
            className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded"
            style={{ backgroundColor: s.bg, color: s.text, borderColor: s.border }}
            data-testid={`kb-category-${value}`}
        >
            {KB_CATEGORY_LABEL[value]}
        </span>
    );
}

function DocsTable({ docs, onDownload, emptyText, showCategory = false }) {
    if (!docs.length) return <div className="text-sm text-slate-500 px-4 py-6" data-testid="docs-empty">{emptyText}</div>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="documents-table">
                <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="table-header-cell">Title</th>
                        <th className="table-header-cell">File</th>
                        {showCategory && <th className="table-header-cell">Type</th>}
                        <th className="table-header-cell">Visibility</th>
                        <th className="table-header-cell">Indexed</th>
                        <th className="table-header-cell">Uploaded</th>
                        <th className="table-header-cell"></th>
                    </tr>
                </thead>
                <tbody>
                    {docs.map((d) => (
                        <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`document-row-${d.id}`}>
                            <td className="table-cell-tight font-medium text-slate-900">{d.title}</td>
                            <td className="table-cell-tight text-slate-700">{d.original_filename}</td>
                            {showCategory && (
                                <td className="table-cell-tight"><CategoryPill value={d.kb_category} /></td>
                            )}
                            <td className="table-cell-tight"><PrivacyBadge visibility={d.visibility} /></td>
                            <td className="table-cell-tight text-xs text-slate-600">{d.is_indexed ? "Yes" : d.index_note ? <span title={d.index_note} data-testid={`not-indexed-${d.id}`}>Not yet</span> : "—"}</td>
                            <td className="table-cell-tight text-slate-500">{new Date(d.created_at).toLocaleDateString()}</td>
                            <td className="table-cell-tight">
                                <button
                                    type="button"
                                    onClick={() => onDownload(d)}
                                    className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 underline"
                                    data-testid={`download-document-${d.id}`}
                                >
                                    <Download size={12} /> Download
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function uploadVisibilityOptions(user, scope = "private", linkedCase = null) {
    const options = [{ value: "private", label: "Private" }];
    const canUseHousehold = scope !== "case_evidence"
        || (["household", "estate_shared"].includes(linkedCase?.visibility) && linkedCase?.household_id);
    const hasHouseholdContext = Boolean(user?.household_id)
        || (scope === "case_evidence" && user?.role === "estate_admin" && Boolean(linkedCase?.household_id));
    if (hasHouseholdContext && canUseHousehold) {
        options.push({ value: "household", label: "Household" });
    }
    return options;
}

export default function Documents() {
    const { user } = useAuth();
    const location = useLocation();
    const canUploadDocuments = ["resident", "household_admin", "estate_admin"].includes(user?.role);
    const canViewResidentEvidenceTab = user?.role !== "agency_viewer";
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [indexing, setIndexing] = useState(false);
    const [includePrivateAdmin, setIncludePrivateAdmin] = useState(false);
    const [adminAccessReason, setAdminAccessReason] = useState("");
    const [uploadCase, setUploadCase] = useState(null);
    const fileRef = useRef(null);
    const [form, setForm] = useState({
        title: "",
        description: "",
        scope: user?.role === "estate_admin" ? "estate_kb" : "private",
        kb_category: "governing",
        visibility: "private",
        case_id: "",
    });

    const fetchDocuments = useCallback(async ({ includePrivate = false, reason = "" } = {}) => {
        setLoading(true);
        try {
            const params = {};
            if (user?.role === "estate_admin" && includePrivate) {
                params.include_private_admin = true;
                params.access_reason = reason.trim();
            }
            const res = await api.get("/documents", { params });
            setDocs(res.data || []);
        } catch {
            toast.error("Failed to load documents");
        } finally {
            setLoading(false);
        }
    }, [user?.role]);

    const fetchAll = () => fetchDocuments({
        includePrivate: includePrivateAdmin,
        reason: adminAccessReason,
    });

    useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

    const loadPrivateAdminDocuments = async () => {
        const reason = adminAccessReason.trim();
        if (reason.length < 8) {
            toast.error("Add a specific reason for private resident metadata access");
            return;
        }
        await fetchDocuments({ includePrivate: true, reason });
        setIncludePrivateAdmin(true);
    };

    const clearPrivateAdminDocuments = async () => {
        setIncludePrivateAdmin(false);
        setAdminAccessReason("");
        await fetchDocuments();
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("upload") !== "1") return;
        if (!canUploadDocuments) return;

        const requestedScope = params.get("scope") || "private";
        const requestedCaseId = params.get("case_id") || "";
        const scope = requestedScope === "estate_kb" && user?.role === "estate_admin"
            ? "estate_kb"
            : requestedScope === "case_evidence" && requestedCaseId
                ? "case_evidence"
                : "private";
        setForm({
            title: params.get("title") || "",
            description: params.get("description") || "",
            scope,
            kb_category: params.get("kb_category") || "governing",
            visibility: "private",
            case_id: requestedCaseId,
        });
        setOpen(true);
    }, [location.search, user?.role, canUploadDocuments]);

    useEffect(() => {
        if (!form.case_id || form.scope !== "case_evidence") {
            setUploadCase(null);
            return;
        }
        let cancelled = false;
        api.get(`/cases/${form.case_id}`)
            .then((res) => {
                if (cancelled) return;
                setUploadCase(res.data);
                const allowed = uploadVisibilityOptions(user, "case_evidence", res.data).some((option) => option.value === form.visibility);
                if (!allowed) {
                    setForm((current) => ({ ...current, visibility: "private" }));
                }
            })
            .catch(() => {
                if (!cancelled) setUploadCase(null);
            });
        return () => {
            cancelled = true;
        };
    }, [form.case_id, form.scope, form.visibility, user]);

    const onUpload = async (e) => {
        e.preventDefault();
        if (!canUploadDocuments) {
            toast.error("Agency viewers have read-only document access");
            return;
        }
        const file = fileRef.current?.files?.[0];
        if (!file) {
            toast.error("Choose a file");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", form.title);
        fd.append("description", form.description);
        fd.append("scope", form.scope);
        if (form.scope === "estate_kb") {
            fd.append("kb_category", form.kb_category);
        }
        if (form.case_id) {
            fd.append("case_id", form.case_id);
        }
        const residentVisibility = uploadVisibilityOptions(user, form.scope, uploadCase).some((option) => option.value === form.visibility)
            ? form.visibility
            : "private";
        fd.append("visibility", form.scope === "estate_kb" ? "estate_shared" : residentVisibility);
        setSubmitting(true);
        try {
            const res = await api.post("/documents/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (res.data?.index_note) {
                toast.warning(res.data.index_note);
            }
            if (res.data?.extraction_warning) {
                toast.warning(res.data.extraction_warning);
            } else {
                toast.success("Document uploaded");
            }
            setOpen(false);
            setForm({
                title: "",
                description: "",
                scope: form.scope === "case_evidence" ? "private" : form.scope,
                kb_category: "governing",
                visibility: "private",
                case_id: "",
            });
            if (fileRef.current) fileRef.current.value = "";
            fetchAll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setSubmitting(false);
        }
    };

    const fetchDownload = async (doc, accessReason = "") => {
        const params = accessReason ? { access_reason: accessReason } : {};
        return api.get(`/documents/${doc.id}/download`, { responseType: "blob", params });
    };

    const onDownload = async (doc) => {
        try {
            let res;
            try {
                res = await fetchDownload(doc);
            } catch (err) {
                if (user?.role === "estate_admin" && err?.response?.status === 400) {
                    const reason = window.prompt("Reason for downloading this private resident document:");
                    if (!reason) throw err;
                    res = await fetchDownload(doc, reason);
                } else {
                    throw err;
                }
            }
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = doc.original_filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Download failed");
        }
    };

    const indexPending = async () => {
        setIndexing(true);
        try {
            const res = await api.post("/admin/documents/index-pending");
            const failed = res.data?.failed?.length || 0;
            toast.success(`Indexed ${res.data?.indexed?.length || 0} document(s)${failed ? `, ${failed} failed` : ""}`);
            fetchAll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not index pending documents");
        } finally {
            setIndexing(false);
        }
    };

    const governing = docs.filter((d) => d.scope === "estate_kb" && d.kb_category === "governing");
    const precedents = docs.filter(
        (d) => d.scope === "estate_kb" && (d.kb_category === "precedent_successful" || d.kb_category === "precedent_failed")
    );
    // KB docs without a category (e.g. seeded map images) are bucketed under governing for now.
    const uncategorised = docs.filter((d) => d.scope === "estate_kb" && !d.kb_category);
    const governingAll = [...governing, ...uncategorised];
    const privateDocs = docs.filter((d) => d.scope === "private" || d.scope === "case_evidence");
    const pendingIndexCount = docs.filter((d) => !d.is_indexed && (d.content_type?.startsWith("text/") || d.content_type === "application/pdf" || d.content_type?.startsWith("image/"))).length;

    return (
        <div className="space-y-6" data-testid="documents-page">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Documents</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Knowledge & evidence</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Upload a service-charge statement, PCN, or letter here. Your files stay private
                        unless you share them. Figures are not treated as fact until someone confirms them.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {user?.role === "estate_admin" && (
                        <Button type="button" variant="outline" onClick={indexPending} disabled={indexing} data-testid="index-pending-documents-button">
                            {indexing ? "Indexing..." : `Index pending${pendingIndexCount ? ` (${pendingIndexCount})` : ""}`}
                        </Button>
                    )}
                    <Dialog open={canUploadDocuments && open} onOpenChange={(next) => canUploadDocuments && setOpen(next)}>
                        {canUploadDocuments && (
                            <DialogTrigger asChild>
                                <Button className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="upload-document-button">
                                    <Upload size={16} className="mr-2" /> Upload
                                </Button>
                            </DialogTrigger>
                        )}
                    <DialogContent data-testid="upload-dialog">
                        <DialogHeader>
                            <DialogTitle>Upload document</DialogTitle>
                            <DialogDescription>
                                Up to 15MB. PDF and text files are auto-indexed for the parking assistant to cite.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={onUpload} className="space-y-4">
                            <div className="space-y-1.5">
                                <Label>File</Label>
                                <Input type="file" ref={fileRef} required data-testid="upload-file-input" />
                                <div className="text-xs text-slate-500">Max 15MB. PDFs and text files are auto-indexed for AI search.</div>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="doc-title">Title</Label>
                                <Input id="doc-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="upload-title-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="doc-desc">Description (optional)</Label>
                                <Textarea id="doc-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="upload-description-input" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label>Scope</Label>
                                    <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v, visibility: "private" })}>
                                        <SelectTrigger data-testid="upload-scope-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="private">Private (mine)</SelectItem>
                                            {form.case_id && (
                                                <SelectItem value="case_evidence">Case evidence</SelectItem>
                                            )}
                                            {user?.role === "estate_admin" && (
                                                <SelectItem value="estate_kb">Estate Knowledge Base</SelectItem>
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Visibility</Label>
                                    <Select
                                        value={form.scope === "estate_kb"
                                            ? "estate_shared"
                                            : uploadVisibilityOptions(user, form.scope, uploadCase).some((option) => option.value === form.visibility)
                                                ? form.visibility
                                                : "private"}
                                        onValueChange={(v) => setForm({ ...form, visibility: v })}
                                        disabled={form.scope === "estate_kb"}
                                    >
                                        <SelectTrigger data-testid="upload-visibility-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {uploadVisibilityOptions(user, form.scope, uploadCase).map((option) => (
                                                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                            ))}
                                            {form.scope === "estate_kb" && <SelectItem value="estate_shared">Estate-shared</SelectItem>}
                                        </SelectContent>
                                    </Select>
                                    {form.scope !== "estate_kb" && (
                                        <div className="text-xs text-slate-500">
                                            Resident evidence starts private or household-visible. Share wider from a reviewed case-sharing action or audited document sharing step.
                                        </div>
                                    )}
                                </div>
                            </div>
                            {form.scope === "estate_kb" && (
                                <div className="space-y-1.5">
                                    <Label>KB category</Label>
                                    <Select
                                        value={form.kb_category}
                                        onValueChange={(v) => setForm({ ...form, kb_category: v })}
                                    >
                                        <SelectTrigger data-testid="upload-kb-category-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="governing">Governing rule (estate / agency / contract)</SelectItem>
                                            <SelectItem value="precedent_successful">Past appeal — UPHELD</SelectItem>
                                            <SelectItem value="precedent_failed">Past appeal — REJECTED</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <div className="text-xs text-slate-500">
                                        Governing rules and resident appeal precedents are kept separate so the AI can cite them correctly.
                                    </div>
                                </div>
                            )}
                            {form.scope === "case_evidence" && (
                                <div className="text-xs text-slate-500 border border-slate-200 rounded-md p-3 bg-slate-50" data-testid="upload-case-evidence-note">
                                    This upload will be linked to the selected case. Visibility choices are limited by the case visibility, and wider sharing needs a reviewed sharing action.
                                </div>
                            )}
                            <DialogFooter>
                                <Button type="submit" disabled={submitting} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="submit-upload-button">
                                    {submitting ? "Uploading…" : "Upload"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                    </Dialog>
                </div>
            </div>

            <Tabs defaultValue="kb">
                {user?.role === "estate_admin" && (
                    <div className="border border-slate-200 bg-white rounded-lg p-4 mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between" data-testid="admin-private-documents-control">
                        <div className="flex items-start gap-3">
                            <Shield size={18} className="text-slate-600 mt-0.5" />
                            <div>
                                <div className="text-sm font-semibold text-slate-900">Private resident metadata</div>
                                <div className="text-xs text-slate-500 max-w-2xl">
                                    Hidden from the normal document list. Loading it records an audit entry with your reason.
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                            <div className="space-y-1">
                                <Label htmlFor="admin-doc-access-reason" className="text-xs">Access reason</Label>
                                <Input
                                    id="admin-doc-access-reason"
                                    value={adminAccessReason}
                                    onChange={(e) => setAdminAccessReason(e.target.value)}
                                    placeholder="e.g. Investigating resident privacy request"
                                    className="sm:w-80"
                                    data-testid="admin-doc-access-reason"
                                    disabled={includePrivateAdmin}
                                />
                            </div>
                            {includePrivateAdmin ? (
                                <Button type="button" variant="outline" onClick={clearPrivateAdminDocuments} data-testid="clear-private-documents-button">
                                    Hide private metadata
                                </Button>
                            ) : (
                                <Button type="button" variant="outline" onClick={loadPrivateAdminDocuments} data-testid="load-private-documents-button">
                                    Load private metadata
                                </Button>
                            )}
                        </div>
                    </div>
                )}
                <TabsList data-testid="documents-tabs">
                    <TabsTrigger value="kb" data-testid="tab-kb">Governing rules ({governingAll.length})</TabsTrigger>
                    <TabsTrigger value="precedents" data-testid="tab-precedents">Appeal precedents ({precedents.length})</TabsTrigger>
                    {canViewResidentEvidenceTab && (
                        <TabsTrigger value="private" data-testid="tab-private">{user?.role === "estate_admin" ? "Resident evidence" : "My documents"} ({privateDocs.length})</TabsTrigger>
                    )}
                </TabsList>
                <TabsContent value="kb">
                    <div className="text-xs text-slate-500 mb-2 px-1">
                        Estate-wide rules and policies that govern parking, communal areas and the operator contract. The AI uses these as the legal yardstick.
                    </div>
                    <div className="border border-slate-200 rounded-lg bg-white">
                        {loading ? (
                            <div className="text-sm text-slate-500 px-4 py-6">Loading…</div>
                        ) : (
                            <DocsTable docs={governingAll} onDownload={onDownload} emptyText="No governing rules in the KB yet." />
                        )}
                    </div>
                </TabsContent>
                <TabsContent value="precedents">
                    <div className="text-xs text-slate-500 mb-2 px-1">
                        Past resident appeals — both upheld and rejected outcomes — used to gauge how similar arguments have fared. These are NOT rules, they are case history.
                    </div>
                    <div className="border border-slate-200 rounded-lg bg-white">
                        {loading ? (
                            <div className="text-sm text-slate-500 px-4 py-6">Loading…</div>
                        ) : (
                            <DocsTable docs={precedents} onDownload={onDownload} emptyText="No appeal precedents recorded yet." showCategory />
                        )}
                    </div>
                </TabsContent>
                {canViewResidentEvidenceTab && (
                <TabsContent value="private">
                    <div className="border border-slate-200 rounded-lg bg-white">
                        {loading ? (
                            <div className="text-sm text-slate-500 px-4 py-6">Loading…</div>
                        ) : (
                            <DocsTable docs={privateDocs} onDownload={onDownload} emptyText="No private documents yet." />
                        )}
                    </div>
                </TabsContent>
                )}
            </Tabs>
        </div>
    );
}
